/**
 * Typed access to the rules data in src/manifests/*.json.
 *
 * The JSON files are the single source of truth for every number the game
 * needs (tile inventories, VP tables, track lengths). Each carries a
 * `_source` (where in the rulebook it comes from) and, while values are still
 * placeholders, a `_todo` list. `pnpm manifests:todo` at the repo root lists
 * every outstanding TODO. Never write a scoring test against a `_todo` value.
 */
import mat from './manifests/mat.json' with { type: 'json' };
import setup from './manifests/setup.json' with { type: 'json' };
import timeTrack from './manifests/time-track.json' with { type: 'json' };
import scoring from './manifests/scoring.json' with { type: 'json' };
import senateTrack from './manifests/senate-track.json' with { type: 'json' };
import ships from './manifests/ships.json' with { type: 'json' };
import commodities from './manifests/commodities.json' with { type: 'json' };
import demandTiles from './manifests/demand-tiles.json' with { type: 'json' };
import forumTiles from './manifests/forum-tiles.json' with { type: 'json' };
import extraActionTiles from './manifests/extra-action-tiles.json' with { type: 'json' };
import constructionTiles from './manifests/construction-tiles.json' with { type: 'json' };
import bonusTiles from './manifests/bonus-tiles.json' with { type: 'json' };
import trajanTiles from './manifests/trajan-tiles.json' with { type: 'json' };
import provinces from './manifests/provinces.json' with { type: 'json' };
import { ACTIONS, COLORS, TRAY_COUNT } from './types.js';
// ---------------------------------------------------------------------------
// Expected component counts from the rulebook. If a manifest disagrees, the
// engine refuses to load rather than silently playing a different game.
// ---------------------------------------------------------------------------
export const EXPECTED_COUNTS = {
    trajanTiles: 54,
    forumTiles: 70,
    extraActionTiles: 12,
    constructionTiles: 20,
    demandTiles: 15,
    bonusTiles: 12,
    commodityCards: 60,
    constructionSites: 20,
    ships: 3,
};
function assert(condition, message) {
    if (!condition)
        throw new Error(`manifest error: ${message}`);
}
function isColor(x) {
    return typeof x === 'string' && COLORS.includes(x);
}
function isAction(x) {
    return typeof x === 'string' && ACTIONS.includes(x);
}
// ---------------------------------------------------------------------------
// Expansion: compact "groups with counts" → concrete tile lists with ids.
// ---------------------------------------------------------------------------
function expandForumTiles() {
    const out = [];
    let n = 0;
    for (const g of forumTiles.groups) {
        for (let i = 0; i < g.count; i++) {
            n += 1;
            const id = `forum-${String(n).padStart(2, '0')}`;
            if (g.kind === 'demand')
                out.push({ id, kind: 'demand', icon: g.icon });
            else if (g.kind === 'senate')
                out.push({ id, kind: 'senate', votes: g.votes });
            else if (g.kind === 'wild-commodity' ||
                g.kind === 'wild-demand' ||
                g.kind === 'wild-construction' ||
                g.kind === 'wild-extra-action')
                out.push({ id, kind: g.kind });
            else
                assert(false, `unknown forum tile kind ${String(g.kind)}`);
        }
    }
    return out;
}
function expandExtraActionTiles() {
    const out = [];
    for (const g of extraActionTiles.groups) {
        assert(isAction(g.action), `bad extra-action tile action ${g.action}`);
        for (let i = 0; i < g.count; i++) {
            out.push({ id: `extra-${g.action}-${i + 1}`, action: g.action, plus2: false });
        }
    }
    return out;
}
function expandDemandTiles() {
    const out = [];
    for (const [icon, count] of Object.entries(demandTiles.counts)) {
        for (let i = 0; i < count; i++)
            out.push({ id: `demand-${icon}-${i + 1}`, icon });
    }
    return out;
}
function expandCommodityCards() {
    const out = [];
    for (const type of commodities.types) {
        for (let i = 0; i < commodities.copiesPerType; i++)
            out.push({ id: `${type}-${i + 1}`, type });
    }
    return out;
}
function typedTrajanTiles() {
    return trajanTiles.tiles.map((t) => {
        const [a, b] = t.colors;
        assert(isColor(a) && isColor(b), `bad colours on ${t.id}`);
        const tile = {
            id: t.id,
            kind: t.kind,
            colors: [a, b],
            vp: t.vp,
        };
        if ('icon' in t && typeof t.icon === 'string')
            tile.icon = t.icon;
        if ('permanent' in t && t.permanent)
            tile.permanent = true;
        if ('tokens' in t && (t.tokens === 1 || t.tokens === 2))
            tile.tokens = t.tokens;
        return tile;
    });
}
function build() {
    const trayActions = mat.trayActions;
    assert(trayActions.length === TRAY_COUNT, 'mat.trayActions must list six actions');
    assert(trayActions.every(isAction) && new Set(trayActions).size === TRAY_COUNT, 'mat.trayActions must be the six distinct actions');
    const trajan = typedTrajanTiles();
    const forum = expandForumTiles();
    const extra = expandExtraActionTiles();
    const construction = constructionTiles.tiles;
    const bonus = bonusTiles.tiles.map((b) => {
        const tile = {
            id: b.id,
            kind: b.kind,
            yellow: b.yellow,
            grey: b.grey,
        };
        if ('icon' in b && typeof b.icon === 'string')
            tile.icon = b.icon;
        if ('commodities' in b && Array.isArray(b.commodities))
            tile.commodities = [...b.commodities];
        return tile;
    });
    const demand = expandDemandTiles();
    const commodity = expandCommodityCards();
    assert(trajan.length === EXPECTED_COUNTS.trajanTiles, `expected 54 Trajan tiles, got ${trajan.length}`);
    assert(forum.length === EXPECTED_COUNTS.forumTiles, `expected 70 forum tiles, got ${forum.length}`);
    assert(extra.length === EXPECTED_COUNTS.extraActionTiles, `expected 12 extra-action tiles, got ${extra.length}`);
    assert(construction.length === EXPECTED_COUNTS.constructionTiles, `expected 20 construction tiles, got ${construction.length}`);
    assert(bonus.length === EXPECTED_COUNTS.bonusTiles, `expected 12 bonus tiles, got ${bonus.length}`);
    assert(demand.length === EXPECTED_COUNTS.demandTiles, `expected 15 demand tiles, got ${demand.length}`);
    assert(commodity.length === EXPECTED_COUNTS.commodityCards, `expected 60 commodity cards, got ${commodity.length}`);
    assert(ships.ships.length === EXPECTED_COUNTS.ships, 'expected 3 ships');
    assert(constructionTiles.grid.columns * constructionTiles.grid.rowsByPlayerCount['4'] ===
        EXPECTED_COUNTS.constructionSites, 'the four-player construction district must have 20 sites');
    assert(trajanTiles.stacks.length === TRAY_COUNT, 'there are six Trajan stacks');
    for (const t of trajan)
        assert(trajanTiles.stacks.includes(t.kind), `Trajan tile ${t.id} has unknown kind`);
    const commodityTypes = new Set(commodities.types);
    for (const b of bonus) {
        if (b.kind === 'per-display-card') {
            assert(b.commodities && b.commodities.length > 0, `bonus tile ${b.id} must name the commodity it scores`);
            for (const c of b.commodities ?? [])
                assert(commodityTypes.has(c), `${b.id} names unknown commodity ${c}`);
        }
        else {
            assert(!b.commodities, `bonus tile ${b.id} should not name commodities`);
        }
    }
    const ids = [
        ...trajan,
        ...forum,
        ...extra,
        ...construction,
        ...bonus,
        ...demand,
        ...commodity,
    ].map((t) => t.id);
    assert(new Set(ids).size === ids.length, 'tile and card ids must be globally unique');
    const provinceIds = new Set(provinces.provinces.map((p) => p.id));
    for (const p of provinces.provinces) {
        for (const adj of p.adjacent)
            assert(provinceIds.has(adj), `province ${p.id} adjacent to unknown ${adj}`);
    }
    for (const c of provinces.campAdjacent)
        assert(provinceIds.has(c), `camp adjacent to unknown ${c}`);
    const lapByPlayerCount = {
        2: timeTrack.lapByPlayerCount['2'],
        3: timeTrack.lapByPlayerCount['3'],
        4: timeTrack.lapByPlayerCount['4'],
    };
    for (const lap of Object.values(lapByPlayerCount))
        assert(lap >= 1, 'a time-track lap needs at least one space');
    const rowsByPlayerCount = {
        2: constructionTiles.grid.rowsByPlayerCount['2'],
        3: constructionTiles.grid.rowsByPlayerCount['3'],
        4: constructionTiles.grid.rowsByPlayerCount['4'],
    };
    assert(mat.extraActionSpaces.every(isAction) && new Set(mat.extraActionSpaces).size === TRAY_COUNT, 'mat.extraActionSpaces must be the six distinct actions');
    for (const s of ships.ships) {
        assert(s.set === 'identical' || s.set === 'different' || s.set === 'pairs', `ship ${s.id} has unknown set`);
    }
    return {
        trayActions,
        trajanSlotBesideTray: mat.trajanSlotBesideTray,
        initialTrajanSlots: mat.initialTrajanSlots,
        extraActionSpaces: mat.extraActionSpaces,
        maxExtraActionsPerTurn: extraActionTiles.maxExtraActionsPerTurn,
        setup,
        timeTrack: {
            lapByPlayerCount,
            roundsPerQuarter: timeTrack.roundsPerQuarter,
            quarters: timeTrack.quarters,
            demandsRevealedAfterRounds: timeTrack.demandsRevealedAfterRounds,
        },
        scoring,
        senateTrack,
        ships: ships.ships,
        provinces: provinces.provinces,
        campAdjacent: provinces.campAdjacent,
        camp: provinces.camp,
        constructionGrid: { columns: constructionTiles.grid.columns, rowsByPlayerCount },
        firstOfTypeBonus: constructionTiles.firstOfTypeBonus,
        trajanRewards: trajanTiles.rewards,
        tiles: {
            trajan,
            trajanStacks: trajanTiles.stacks,
            forum,
            extraAction: extra,
            construction,
            bonus,
            demand,
            commodity,
        },
    };
}
export const MANIFESTS = build();
/** Every `_todo` still open across the manifest files, for `pnpm manifests:todo`. */
export function manifestTodos() {
    const files = [
        ['mat.json', mat],
        ['setup.json', setup],
        ['time-track.json', timeTrack],
        ['scoring.json', scoring],
        ['senate-track.json', senateTrack],
        ['ships.json', ships],
        ['commodities.json', commodities],
        ['demand-tiles.json', demandTiles],
        ['forum-tiles.json', forumTiles],
        ['extra-action-tiles.json', extraActionTiles],
        ['construction-tiles.json', constructionTiles],
        ['bonus-tiles.json', bonusTiles],
        ['trajan-tiles.json', trajanTiles],
        ['provinces.json', provinces],
    ];
    return files.flatMap(([file, m]) => {
        const todo = m['_todo'];
        return Array.isArray(todo) ? todo.map((t) => ({ file, todo: t })) : [];
    });
}
//# sourceMappingURL=manifests.js.map